import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth, user as userApi } from '../utils/api';
import api from '../utils/api';
import LoadingSpinner from '../components/LoadingSpinner';

// Detect measurement system from browser locale
// Imperial: US, Liberia (LR), Myanmar (MM)
// Metric: everywhere else
const detectMeasurementSystem = () => {
  try {
    const locale = navigator.language || navigator.userLanguage || 'en-US';
    const parts = locale.split('-');
    const country = parts.length > 1 ? parts[parts.length - 1].toUpperCase() : '';
    const imperialCountries = ['US', 'LR', 'MM'];
    return imperialCountries.includes(country) ? 'imperial' : 'metric';
  } catch {
    return 'imperial';
  }
};

// Helper to clear user-specific caches (prevents cross-user data leakage)
const clearUserCaches = () => {
  localStorage.removeItem('recipesCache');
  localStorage.removeItem('recipesCacheUserId');
  localStorage.removeItem('recipesCacheTime');
  localStorage.removeItem('userCache');
  localStorage.removeItem('currentMealOrder');
  localStorage.removeItem('upNextOrder');
  localStorage.removeItem('miseEnPlaceContent');
  localStorage.removeItem('miseEnPlaceCacheKey');
  localStorage.removeItem('miseCheckedItems');
};

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      try {
        // Extract session_id from URL fragment
        const hash = location.hash;
        const params = new URLSearchParams(hash.substring(1));
        const sessionId = params.get('session_id');

        if (!sessionId) {
          navigate('/login');
          return;
        }

        // Exchange session_id for session_token
        const response = await auth.exchangeSession(sessionId);
        const { user, session_token } = response.data;
        
        // CRITICAL: Check if this is a different user than what's cached
        // If so, clear all user-specific caches to prevent cross-user data bugs
        const cachedUserId = localStorage.getItem('recipesCacheUserId');
        if (cachedUserId && cachedUserId !== user.user_id) {
          console.log('Different user logged in - clearing caches');
          clearUserCaches();
        }

        // Store token in localStorage as backup for Safari ITP
        if (session_token) {
          localStorage.setItem('session_token', session_token);
        }

        // Auto-detect measurement system for new users based on locale
        // Only set if user doesn't already have one (new signup)
        if (!user.measurement_system || user.measurement_system === 'imperial') {
          try {
            const detected = detectMeasurementSystem();
            if (detected !== (user.measurement_system || 'imperial')) {
              await userApi.updateMeasurementSystem(detected);
            }
          } catch (e) {
            // Non-critical, skip silently
          }
        }

        // Link landing page session for analytics (if exists)
        try {
          const landingSessionId = localStorage.getItem('honey_landing_session');
          if (landingSessionId) {
            await api.post('/landing/link-session', { session_id: landingSessionId });
            // Don't remove it - keep for attribution tracking
          }
        } catch (e) {
          console.log('Landing session link skipped:', e);
        }

        // Check for import URL in cookie (most reliable method)
        let destination = '/recipe-box';
        const cookies = document.cookie.split(';');
        for (const cookie of cookies) {
          const [name, value] = cookie.trim().split('=');
          if (name === 'honey_import_url' && value) {
            const importUrl = decodeURIComponent(value);
            destination = `/import?url=${encodeURIComponent(importUrl)}`;
            // Clear the cookie
            document.cookie = 'honey_import_url=; path=/; max-age=0';
            break;
          }
        }
        
        // Check if we need to auto-add a shared recipe after login
        const autoAddRecipeId = sessionStorage.getItem('autoAddSharedRecipe');
        if (autoAddRecipeId) {
          sessionStorage.removeItem('autoAddSharedRecipe');
          sessionStorage.removeItem('intendedUrl'); // Clear this too since we're handling it
          
          try {
            // Auto-add the shared recipe to user's box
            // Use explicit Authorization header since we just set the token
            const addResponse = await api.post(`/shared/${autoAddRecipeId}/add`, {}, {
              headers: {
                'Authorization': `Bearer ${session_token}`
              }
            });
            // Navigate directly to the new recipe
            navigate(`/recipe/${addResponse.data.recipe_id}`, { 
              state: { user, welcomeMessage: 'Recipe added to your box!' }, 
              replace: true 
            });
            return;
          } catch (addError) {
            console.error('Failed to auto-add recipe:', addError);
            // If auto-add fails, still go to the shared recipe page so they can try again
            navigate(`/shared/${autoAddRecipeId}`, { state: { user }, replace: true });
            return;
          }
        }
        
        // Fallback to other storage methods
        if (destination === '/recipe-box') {
          const intendedUrl = sessionStorage.getItem('intendedUrl') || localStorage.getItem('importReturnUrl');
          sessionStorage.removeItem('intendedUrl');
          localStorage.removeItem('importReturnUrl');
          if (intendedUrl) {
            destination = intendedUrl;
          }
        }
        
        navigate(destination, { state: { user }, replace: true });
      } catch (error) {
        console.error('Auth callback error:', error);
        navigate('/login');
      }
    };

    processSession();
  }, [location, navigate]);

  return (
    <LoadingSpinner message="Setting up your Honey..." fullScreen />
  );
};

export default AuthCallback;